import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Loads GA4 + Meta Pixel scripts once (if env IDs are provided) and
 * tracks a page_view on every route change.
 *
 * Env vars (frontend/.env):
 *   REACT_APP_GA4_ID=G-XXXXXXXXXX
 *   REACT_APP_META_PIXEL_ID=1234567890
 *
 * Leave either blank to disable that provider.
 */
// Strict format gates so a placeholder like "none" or "i dont have"
// doesn't accidentally initialize a tracker. GA4 looks like "G-XXXXX",
// Meta Pixel is 15-16 digits.
const GA4_RE = /^G-[A-Z0-9]{6,}$/i;
const META_RE = /^\d{10,20}$/;
const _rawGa = (process.env.REACT_APP_GA4_ID || "").trim();
const _rawPx = (process.env.REACT_APP_META_PIXEL_ID || "").trim();
const GA4_ID = GA4_RE.test(_rawGa) ? _rawGa : "";
const META_PIXEL_ID = META_RE.test(_rawPx) ? _rawPx : "";

const CONSENT_KEY = "galeriecop.cookieConsent";
const readConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return { analytics: false, marketing: false };
    const c = JSON.parse(raw) || {};
    const ch = c.choices || {};
    return { analytics: !!ch.analytics, marketing: !!ch.marketing };
  } catch {
    return { analytics: false, marketing: false };
  }
};

let _loaded = false;

const injectScript = (src, attrs = {}) => {
  const s = document.createElement("script");
  s.src = src;
  s.async = true;
  Object.entries(attrs).forEach(([k, v]) => s.setAttribute(k, v));
  document.head.appendChild(s);
};

const loadOnce = () => {
  if (_loaded) return;
  const { analytics, marketing } = readConsent();
  // Nothing to do until the user grants at least one bucket.
  if (!analytics && !marketing) return;
  _loaded = true;

  // ---------- GA4 (analytics bucket) ----------
  if (GA4_ID && analytics) {
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    // SPA — turn off automatic page_view, we fire it on route change.
    window.gtag("config", GA4_ID, { send_page_view: false });
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`);
  }

  // ---------- Meta Pixel (marketing bucket) ----------
  if (META_PIXEL_ID && marketing) {
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){
      if(f.fbq)return;
      n=f.fbq=function(){ n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments) };
      if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
      t=b.createElement(e);t.async=!0;t.src=v;
      s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq("init", META_PIXEL_ID);
    window.fbq("track", "PageView");
  }
};

const AnalyticsLoader = () => {
  const loc = useLocation();

  useEffect(() => {
    if (!GA4_ID && !META_PIXEL_ID) return;
    loadOnce();
    // Re-check when the user updates their cookie preferences in-session.
    const onConsent = () => loadOnce();
    window.addEventListener("galeriecop:consent-updated", onConsent);
    return () => window.removeEventListener("galeriecop:consent-updated", onConsent);
  }, []);

  // Route-change page_view (only fires if trackers were loaded).
  useEffect(() => {
    if (!GA4_ID && !META_PIXEL_ID) return;
    const path = loc.pathname + loc.search;
    if (GA4_ID && window.gtag) {
      window.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    if (META_PIXEL_ID && window.fbq) {
      window.fbq("track", "PageView");
    }
  }, [loc.pathname, loc.search]);

  return null;
};

/** Fire a purchase/conversion event from anywhere in the app. */
export const trackPurchase = ({ value, currency = "USD", transactionId, tier }) => {
  try {
    if (GA4_ID && window.gtag) {
      window.gtag("event", "purchase", {
        transaction_id: transactionId,
        value: Number(value) || 0,
        currency,
        items: tier ? [{ item_id: tier, item_name: `GalerieCop ${tier}`, price: value, quantity: 1 }] : undefined,
      });
    }
    if (META_PIXEL_ID && window.fbq) {
      window.fbq("track", "Purchase", {
        value: Number(value) || 0,
        currency,
        content_type: "product",
        content_ids: tier ? [tier] : undefined,
      });
    }
  } catch (e) {
    // Analytics must never block app flow.
    // eslint-disable-next-line no-console
    console.warn("[analytics] trackPurchase failed", e);
  }
};

export default AnalyticsLoader;
